import {
    FC,
    ReactElement,
    useEffect,
    useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { Modal } from 'reactstrap'

import { PacoLogsModal } from '../../../../@paco/compositions'
import { AbsenceFormData } from '../../../../@paco/entities/Absence/Absence'
import { transformLegacyAbsenceToAbsence } from '../../../../@paco/entities/Absence/AbsenceTransformers'
import {
    transformLegacyLeaveOfAbsenceToLeaveOfAbsence,
} from '../../../../@paco/entities/LeaveOfAbsence/LeaveOfAbsenceTransformers'
import {
    addDays,
    addSeconds,
    getEndOfWorkDay,
    getStartOfWorkDay,
} from '../../../../@paco/helpers/date'
import checkPermission from '../../../../@paco/helpers/permissions/checkPermission'
import { setUserSearch } from '../../../../@paco/redux/@interface/globalFilters/globalFiltersReducer'
import { deleteAbsence, patchAbsence } from '../../../../@paco/redux/absences/absencesActions'
import { setError as setAbsencesError } from '../../../../@paco/redux/absences/absencesReducer'
import { getEventLogsByEntityUuid } from '../../../../@paco/redux/eventLogs/eventLogsActions'
import {
    approveLeaveOfAbsence,
    declineLeaveOfAbsence,
    deleteLeaveOfAbsence,
    patchLeaveOfAbsence,
} from '../../../../@paco/redux/leaveOfAbsences/leaveOfAbsencesActions'
import { setError as setLeaveOfAbsencesError } from '../../../../@paco/redux/leaveOfAbsences/leaveOfAbsencesReducer'
import { getPacoPayrollPeriods } from '../../../../@paco/redux/payrollPeriods/payrollPeriodsActions'
import { fetchSpecialDays } from '../../../../@paco/redux/specialDays/specialDaysActions'
import { useTypedSelector } from '../../../../@paco/redux/store'
import { setToast } from '../../../../@paco/redux/toasts/toastsReducer'
import { ScheduleRoutes } from '../../../../@paco/routes/helpers'
import { PrivateRouteGroupRoutes } from '../../../../@paco/routes/routes'
import { TimeModeType, ToastType } from '../../../../@paco/types'
import { PzLeaveType } from '../../../../@paco/types/leaveType'

import NewFilters from '../../../../components/Filters/Filters'
import SidebarPage from '../../../../components/SidebarPage/SidebarPage'
import { LIST_VIEW } from '../../../../constants'
import { translate } from '../../../../helpers/translations/translator'
import {
    Absence,
    LeaveOfAbsence,
    LeaveOfAbsenceEditType,
    LeaveOfAbsenceFormData,
    LeaveType,
    PayrollPeriodViewModel,
    PayrollPeriodWithHours,
    UnavailableToWorkTimeSlot,
    UserWithAvailability,
    WeekWithHours,
} from '../../../../models'
import { getPayrollPeriods, switchMode } from '../../../../redux/app/appActions'
import { setFilter } from '../../../../redux/filter/filterActions'
import { AppFilter } from '../../../../redux/filter-ts/filterModels'
import { setUserPlanningPreference } from '../../../../redux/management/managementActions'
import { setPaginationNumber } from '../../../../redux/pagination/paginationActions'
import { Reducers } from '../../../../redux/reducers'
import { getUsersWithAvailability, planAvailabilityUserToShift } from '../../../../redux/shifts-ts/shiftsActions'
import { TypedDispatch } from '../../../../redux/store'
import { setTimeMode } from '../../../../redux/weekNavigator/weekNavigatorActions'
import DeclineLeaveOfAbsenceForm from '../../../Absences/forms/DeclineLeaveOfAbsenceForm'
import EditAbsenceForm from '../../../Absences/forms/EditAbsenceForm/EditAbsenceForm'
import ResolveLeaveOfAbsenceForm from '../../../Absences/forms/ResolveLeaveOfAbsenceForm/ResolveLeaveOfAbsenceForm'
import AddOrEditAvailabilityTime from '../../../Management/forms/AddOrEditAvailabilityTime/AddOrEditAvailabilityTime'
import { AvailabilityResourceTypes } from '../../components/AvailabilityDays/subcomponents/Availability/Availability'
import ListView from '../../containers/ListView/ListView'
import AssignShiftToUserForm from '../../forms/AssignShiftToUserForm/AssignShiftToUserForm'

const ConnectedListView: FC = (): ReactElement => {
    const dispatch = useDispatch<TypedDispatch>()
    const navigate = useNavigate()

    const { selectedDays, timeMode } = useTypedSelector(state => state.calendarReducer)
    const { payrollPeriods } = useTypedSelector(state => state.pacoPayrollPeriodsReducer)
    const legacyPayrollPeriods = useSelector<Reducers, PayrollPeriodViewModel[]>(state => state.appReducer.payrollPeriods)
    const { specialDays } = useSelector((state: Reducers) => state.specialDaysReducer)
    const { permissions, role: currentUserRole } = useTypedSelector(state => state.authenticatedUserReducer)
    const { departmentOptions, userSearch } = useTypedSelector(state => state.globalFiltersReducer)
    const showEmploymentTypes = useTypedSelector(state => state.globalFiltersReducer.showEmploymentTypes)
    const users = useSelector((state: Reducers) => state.shiftsReducer.usersWithAvailability)
    const shiftsLoading = useSelector((state: Reducers) => state.shiftsReducer.loading)
    const absencesLoading = useSelector((state: Reducers) => state.absencesReducer.loading)
    const { isLoading: pacoAbsencesLoading, error: absencesError } = useTypedSelector(state => state.pacoAbsencesReducer)
    const { isLoading: leaveOfAbsencesLoading, error: leaveOfAbsencesError } = useTypedSelector(state => state.pacoLeaveOfAbsencesReducer)
    const managementLoading = useSelector((state: Reducers) => state.managementReducer.loading)
    const { eventLogsByEntity, isLoading: isLogsLoading } = useSelector((state: Reducers) => state.eventLogsReducer)
    const { isLoading: isShiftPlanningLoading, isEditSuccessful } = useSelector((state: Reducers) => state.shiftPlanningReducer)
    const filter = useSelector((state: Reducers) => state.filterReducer.filter)
    const pagination = useSelector((state: Reducers) => state.paginationReducer)

    const loading = shiftsLoading || absencesLoading || pacoAbsencesLoading || leaveOfAbsencesLoading || managementLoading || isShiftPlanningLoading
    const canAddPlannings = checkPermission(permissions, 'add-new-shift-plannings')
    const canViewShiftConcepts = checkPermission(permissions, 'view-all-shift-concepts')
    const canViewAbsences = checkPermission(permissions, 'view-all-absences')
    const canViewLeaveOfAbsences = checkPermission(permissions, 'view-all-leave-of-absences')

    const [resolveLeaveOfAbsenceForm, setResolveLeaveOfAbsenceForm] = useState(false)
    const [activeLeaveOfAbsence, setActiveLeaveOfAbsence] = useState<LeaveOfAbsence | null>(null)
    const [declineLeaveOfAbsenceForm, setDeclineLeaveOfAbsenceForm] = useState(false)
    const [editLOAData, setEditLOAData] = useState<LeaveOfAbsenceFormData | null>(null)
    const [editAbsenceForm, setEditAbsenceForm] = useState(false)
    const [activeAbsence, setActiveAbsence] = useState<Absence | null>(null)
    const [editPlanningPreferenceForm, setEditPlanningPreferenceForm] = useState(false)
    const [activePlanningPreference, setActivePlanningPreference] = useState<UnavailableToWorkTimeSlot | null>(null)
    const [addShiftForUserData, setAddShiftForUserData] = useState<{ user: UserWithAvailability, date: Date } | null>(null)
    const [logsModal, setLogsModal] = useState(false)

    const startDate = getStartOfWorkDay(selectedDays[0])
    const endDate = getEndOfWorkDay(selectedDays[selectedDays.length - 1])

    const getUsersData = (): void => {
        dispatch(getUsersWithAvailability(canViewAbsences, canViewLeaveOfAbsences))
    }

    const handleSearchBarChange = (search: string): void => {
        dispatch(setUserSearch(search))
    }

    const handleCalendarTypeChange = (): void => {
        dispatch(switchMode())
    }

    const onSetFilterChange = (newFilter: AppFilter): void => {
        dispatch(setFilter(newFilter, getUsersData))
    }

    const dispatchEditLeaveOfAbsence = async (
        editType: LeaveOfAbsenceEditType,
        data: LeaveOfAbsenceFormData,
        payrollPeriodsWithHours: PayrollPeriodWithHours[],
        weeksWithHours: WeekWithHours[],
        leaveType?: LeaveType | PzLeaveType | null,
    ): Promise<void> => {
        setResolveLeaveOfAbsenceForm(false)

        if (! activeLeaveOfAbsence) {
            return
        }

        switch (editType) {
            case LeaveOfAbsenceEditType.accept:
                await dispatch(approveLeaveOfAbsence(
                    transformLegacyLeaveOfAbsenceToLeaveOfAbsence(activeLeaveOfAbsence),
                    data,
                    activeLeaveOfAbsence.leaveOfAbsenceHours || [],
                    weeksWithHours,
                    leaveType ?? undefined,
                ))
                break
            case LeaveOfAbsenceEditType.delete:
                await dispatch(deleteLeaveOfAbsence(
                    transformLegacyLeaveOfAbsenceToLeaveOfAbsence(activeLeaveOfAbsence),
                ))
                break
            default:
                await dispatch(patchLeaveOfAbsence(
                    transformLegacyLeaveOfAbsenceToLeaveOfAbsence(activeLeaveOfAbsence),
                    data,
                    activeLeaveOfAbsence.leaveOfAbsenceHours || [],
                    weeksWithHours,
                    leaveType ?? undefined,
                ))
                break
        }

        setActiveLeaveOfAbsence(null)
        getUsersData()
    }

    const dispatchDeclineLeaveOfAbsence = async (declineComment: string): Promise<void> => {
        setResolveLeaveOfAbsenceForm(false)
        setDeclineLeaveOfAbsenceForm(false)
        setActiveLeaveOfAbsence(null)
        setEditLOAData(null)

        if (! editLOAData || ! activeLeaveOfAbsence) {
            return
        }

        await dispatch(declineLeaveOfAbsence(
            transformLegacyLeaveOfAbsenceToLeaveOfAbsence(activeLeaveOfAbsence),
            declineComment,
        ))

        getUsersData()
    }

    const addDeclineLeaveOfAbsenceForm = (data: LeaveOfAbsenceFormData): void => {
        setResolveLeaveOfAbsenceForm(false)
        setDeclineLeaveOfAbsenceForm(true)
        setEditLOAData(data)
    }

    const dispatchEditAbsence = async (
        absence: Absence,
        data: AbsenceFormData,
        weeksWithHours: WeekWithHours[],
    ): Promise<void> => {
        setEditAbsenceForm(false)

        await dispatch(patchAbsence(
            transformLegacyAbsenceToAbsence(absence),
            data,
            absence.absenceHours || [],
            weeksWithHours,
        ))

        getUsersData()
    }

    const dispatchDeleteAbsence = async (absence: Absence): Promise<void> => {
        setEditAbsenceForm(false)
        await dispatch(deleteAbsence(transformLegacyAbsenceToAbsence(absence)))
        getUsersData()
    }

    const dispatchAvailabilityTime = (
        weekday: number,
        fromDate: string,
        toDate: string,
        comment: string,
    ): void => {
        const { user, id } = activePlanningPreference as UnavailableToWorkTimeSlot
        setEditPlanningPreferenceForm(false)
        setActivePlanningPreference(null)
        dispatch(setUserPlanningPreference(
            weekday,
            fromDate,
            toDate,
            comment,
            user.id,
            id,
            LIST_VIEW,
        ))
    }

    const dispatchPlanUserToShift = (user: UserWithAvailability, shiftId: string): void => {
        setAddShiftForUserData(null)
        dispatch(planAvailabilityUserToShift(shiftId, user.id))
    }

    const dispatchGetLeaveOfAbsencesLogs = async (id: string): Promise<void> => {
        await dispatch(getEventLogsByEntityUuid(id))
        setLogsModal(true)
    }

    const dispatchCloseLogsModal = (): void => {
        setLogsModal(false)
    }

    const onSubmitLeaveOfAbsenceForm = (
        editType: LeaveOfAbsenceEditType,
        data: LeaveOfAbsenceFormData,
        payrollPeriodsWithHours: PayrollPeriodWithHours[],
        weeksWithHours: WeekWithHours[],
        leaveType?: LeaveType | PzLeaveType | null,
    ): void => {
        if (editType === 'deny') {
            addDeclineLeaveOfAbsenceForm(data)
        } else {
            dispatchEditLeaveOfAbsence(
                editType,
                data,
                payrollPeriodsWithHours,
                weeksWithHours,
                leaveType,
            )
        }
    }

    const onCancelLeaveOfAbsenceForm = (): void => {
        setResolveLeaveOfAbsenceForm(false)
    }

    const onCancelDeclineLeaveOfAbsenceForm = (): void => {
        setDeclineLeaveOfAbsenceForm(false)
        setActiveLeaveOfAbsence(null)
        setEditLOAData(null)
    }

    const onCancelEditAbsence = (): void => {
        setEditAbsenceForm(false)
    }

    const cancelPlanningPreferenceForm = (): void => {
        setEditPlanningPreferenceForm(false)
        setActivePlanningPreference(null)
    }

    const onCancelAddShiftForUserData = (): void => {
        setAddShiftForUserData(null)
    }

    const onResourceClick = (resource: AvailabilityResourceTypes): void => {
        if (resource.type === 'absences') {
            setEditAbsenceForm(true)
            setActiveAbsence(resource as Absence)
        }

        if (resource.type === 'leaveOfAbsences') {
            setResolveLeaveOfAbsenceForm(true)
            setActiveLeaveOfAbsence(resource as LeaveOfAbsence)
        }

        if (resource.type === 'shiftPlannings') {
            navigate(`${PrivateRouteGroupRoutes.shifts()}/${ScheduleRoutes.shift(resource.shift.id)}`)
        }

        if (resource.type === 'UnavailableToWorkTimeSlots') {
            setEditPlanningPreferenceForm(true)
            setActivePlanningPreference(resource as UnavailableToWorkTimeSlot)
        }
    }

    const onAddButtonClick = (date: Date, user: UserWithAvailability): void => {
        setAddShiftForUserData({ user, date })
    }

    useEffect((): void => {
        dispatch(fetchSpecialDays(addDays(startDate, -1), addSeconds(endDate, 1)))
    }, [selectedDays])

    useEffect((): void => {
        // Fall back to 'week' if initial time mode is not supported by this view
        if (timeMode !== TimeModeType.week && timeMode !== TimeModeType.custom) {
            dispatch(setTimeMode(TimeModeType.week))
        }

        if (! legacyPayrollPeriods.length) {
            dispatch(getPayrollPeriods())
        }

        if (! payrollPeriods.length) {
            dispatch(getPacoPayrollPeriods())
        }
    }, [])

    useEffect((): void => {
        dispatch(setPaginationNumber(1))
    }, [departmentOptions, userSearch])

    useEffect((): void => {
        getUsersData()
    }, [selectedDays, departmentOptions, userSearch])

    useEffect((): void => {
        if (isEditSuccessful) {
            getUsersData()
        }
    }, [isEditSuccessful])

    useEffect((): void => {
        if (leaveOfAbsencesError || absencesError) {
            dispatch(setToast({
                text: leaveOfAbsencesError || absencesError,
                type: ToastType.fail,
                shouldFadeOutAutomatically: false,
            }))

            dispatch(setLeaveOfAbsencesError(''))
            dispatch(setAbsencesError(''))
        }
    }, [leaveOfAbsencesError, absencesError])

    return (
        <>
            <SidebarPage
                sidebarContent={(
                    <NewFilters
                        filter={filter}
                        filterBlocks={[
                            'availability',
                            'availability-sorting',
                            'main-department',
                            'employment-type-badge',
                        ]}
                        permissions={permissions}
                        onChange={onSetFilterChange}
                    />
                )}
            >
                <ListView
                    canViewShiftConcepts={canViewShiftConcepts}
                    isFirstRowSticky
                    isFirstColumnSticky
                    isLoading={loading}
                    days={selectedDays}
                    filter={filter}
                    showEmploymentTypes={showEmploymentTypes}
                    users={users}
                    pagination={pagination}
                    canAddPlannings={canAddPlannings}
                    specialDays={specialDays}
                    startDate={startDate}
                    endDate={endDate}
                    onSearchBarChange={handleSearchBarChange}
                    getUsersData={getUsersData}
                    onResourceClick={onResourceClick}
                    onAddButtonClick={onAddButtonClick}
                    onCalendarTypeChange={handleCalendarTypeChange}
                />
            </SidebarPage>
            <Modal size="lg" isOpen={resolveLeaveOfAbsenceForm} className="form-resolve-leave-of-absence">
                <ResolveLeaveOfAbsenceForm
                    absence={activeLeaveOfAbsence}
                    permissions={permissions}
                    currentUserRole={currentUserRole}
                    pacoPayrollPeriods={payrollPeriods}
                    payrollPeriods={legacyPayrollPeriods}
                    onSubmit={onSubmitLeaveOfAbsenceForm}
                    onCancel={onCancelLeaveOfAbsenceForm}
                    onShowLogsClick={dispatchGetLeaveOfAbsencesLogs}
                />
            </Modal>
            <Modal size="lg" isOpen={declineLeaveOfAbsenceForm} className="form-decline-leave-of-absence">
                <DeclineLeaveOfAbsenceForm
                    onSubmit={dispatchDeclineLeaveOfAbsence}
                    onCancel={onCancelDeclineLeaveOfAbsenceForm}
                />
            </Modal>
            <Modal size="lg" isOpen={editAbsenceForm} className="form-resolve-absence">
                <EditAbsenceForm
                    data={activeAbsence as Absence}
                    permissions={permissions}
                    currentUserRole={currentUserRole}
                    pacoPayrollPeriods={payrollPeriods}
                    payrollPeriods={legacyPayrollPeriods}
                    onSubmit={dispatchEditAbsence}
                    onDelete={dispatchDeleteAbsence}
                    onCancel={onCancelEditAbsence}
                />
            </Modal>
            <Modal size="lg" isOpen={editPlanningPreferenceForm} className="form-add-time">
                <AddOrEditAvailabilityTime
                    data={activePlanningPreference}
                    permissions={permissions}
                    onCancel={cancelPlanningPreferenceForm}
                    onSubmit={dispatchAvailabilityTime}
                />
            </Modal>
            <Modal size="lg" isOpen={!! addShiftForUserData} className="form-assign-shift-to-user">
                <AssignShiftToUserForm
                    user={addShiftForUserData ? addShiftForUserData.user : null}
                    date={addShiftForUserData ? addShiftForUserData.date : null}
                    onSubmit={dispatchPlanUserToShift}
                    onCancel={onCancelAddShiftForUserData}
                />
            </Modal>
            <PacoLogsModal
                isLoading={isLogsLoading}
                isOpen={logsModal && ! isLogsLoading}
                title={translate('logs.leaveOfAbsenceLog')}
                emptyMessage={translate('logs.emptyLeaveOfAbsenceLog')}
                logs={eventLogsByEntity}
                onClose={dispatchCloseLogsModal}
            />
        </>
    )
}

export default ConnectedListView
